import { ReactNode, useMemo, useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@design-system';
import {
  claimantKey,
  getCumulativeLtcPayoutSeries,
  getFirstPayoutIndexByClaimant,
} from '@shared/calculations/funding/projection/projectionTotals';
import { FundingPolicyType } from '@shared/appModel';
import { buildPayoutColumnGroups } from './projectionTable/columnGroups';
import { claimantMonthKey, computeClaimantRelativeMonthMap } from './projectionTable/formatters';
import type { PoolDrawdownToDate } from './projectionTable/types';
import { ProjectionTable } from './projectionTable/ProjectionTable';
import { PrePayoutTable } from './projectionTable/PrePayoutTable';
import { getPresentation } from './projectionTable/strategies/registry';

type ClaimantView = 'both' | 'client' | 'partner';

interface MonthlyBreakdownTableProps {
  projection: ClaimProjection;
  policyType: FundingPolicyType | null | undefined;
}

/**
 * Partitions the projection at the first paying month and renders the two
 * halves inside one container with a timeline-style visual: a left-edge
 * rail with marker dots, the "Before benefits begin" section, a
 * "BENEFITS ACTIVATED" pill divider, then the "After benefits begin" section.
 */
export function MonthlyBreakdownTable({ projection, policyType }: MonthlyBreakdownTableProps) {
  // Joint detection — any tagged row anywhere in the projection switches us
  // into per-claimant mode (per-spouse OOP split + view filter). For
  // single-life every row's `claimant` is undefined and this stays false.
  const isJoint = useMemo(
    () => projection.months.some(m => m.context.claimant != null),
    [projection.months],
  );

  const [view, setView] = useState<ClaimantView>('both');

  // Each claimant has their own "benefits begin" boundary — partner can be
  // months or years behind the session client. Per-claimant indexing lets a
  // later-starting spouse's OOP rows surface in the pre-payout table even
  // when the household has been receiving benefits for the other spouse.
  // For single-life every row keys to one sentinel bucket, so this reduces
  // to the legacy global split.
  const firstPayoutIndexByClaimant = useMemo(
    () => getFirstPayoutIndexByClaimant(projection.months),
    [projection.months],
  );

  const { preMonths, payoutMonths } = useMemo(() => {
    const pre: MonthlyEntry[] = [];
    const payout: MonthlyEntry[] = [];
    projection.months.forEach((month, i) => {
      // View filter: hidden for single-life; for joint the toggle selects
      // which spouse's rows render. The toggle is rendered conditionally on
      // `isJoint` below, but we still gate here so the underlying data stays
      // truthful for derived state.
      if (view !== 'both' && month.context.claimant !== view) return;
      const firstPay = firstPayoutIndexByClaimant.get(claimantKey(month));
      if (firstPay == null || i < firstPay) {
        pre.push(month);
      } else {
        payout.push(month);
      }
    });
    return { preMonths: pre, payoutMonths: payout };
  }, [projection.months, firstPayoutIndexByClaimant, view]);

  const presentation = useMemo(() => getPresentation(policyType), [policyType]);

  // Cumulative pool drawdown over the full projection, categorized for the
  // "Drawn to date" column. Prior utilization is derived: the AOB ceiling at
  // claim start (snapshot) minus the first month's actual opening AOB equals
  // the `utilizedToDate` the engine subtracted — works for every product.
  const poolDrawdown = useMemo<PoolDrawdownToDate>(() => {
    const months = projection.months;
    const snapshotByYear = new Map(projection.yearSnapshots.map(s => [s.policyYear, s]));
    const first = months[0];
    let priorUtilization = 0;
    if (first) {
      const snap = snapshotByYear.get(first.context.policyYear);
      if (snap) {
        const aobCeilingAtStart =
          snap.theoreticalLtcPoolCeiling - snap.effectiveContinuationBenefit;
        priorUtilization = Math.max(
          aobCeilingAtStart - first.openState.pools.baseBenefitPool,
          0,
        );
      }
    }
    const byRowKey = new Map<string, { aob: number; cob: number }>();
    let aob = 0;
    let cob = 0;
    for (const m of months) {
      aob += m.deltas.base.depletion;
      cob += m.deltas.eob.depletion;
      byRowKey.set(claimantMonthKey(m.context.claimant, m.context.monthIndex), { aob, cob });
    }
    return { priorUtilization, byRowKey };
  }, [projection.months, projection.yearSnapshots]);

  // Per-claimant "Month of LTC" display map for joint projections. Computed
  // from the full projection (not the pre/payout slice) so partner's first
  // care row reads "Month 1" even when the pre-payout view truncates earlier
  // months. Single-life is a no-op — every row maps to `monthIndex + 1`,
  // matching the prior absolute display.
  const monthDisplayMap = useMemo(
    () => computeClaimantRelativeMonthMap(projection.months),
    [projection.months],
  );

  const payoutGroups = useMemo(
    () =>
      buildPayoutColumnGroups({
        terms: projection.terms,
        yearSnapshots: projection.yearSnapshots,
        presentation,
        poolDrawdown,
        monthDisplayMap,
      }),
    [projection.terms, projection.yearSnapshots, presentation, poolDrawdown, monthDisplayMap],
  );

  // Opening state going into the first row of each table. We now read each
  // table's own first row's `openState` directly: with the per-claimant
  // split (and optional view filter), `preMonths` and `payoutMonths` are no
  // longer guaranteed-contiguous slices of the projection, so the legacy
  // "close of last pre = open of first payout" shortcut no longer holds.
  // Each row's `openState` already encodes the close of whatever ran before
  // it in the original interleaved order, so reading it directly stays
  // correct under both single-life and joint with any view filter.
  const emptyState: MonthlyState = {
    pools: { baseBenefitPool: 0, extensionOfBenefitsPool: 0, deathBenefit: 0 },
    waitingPeriod: { homeDays: 0, facilityDays: 0 },
    strategyState: {},
  };
  const initialState: MonthlyState =
    preMonths[0]?.openState ?? payoutMonths[0]?.openState ?? emptyState;
  const payoutOpenState: MonthlyState = payoutMonths[0]?.openState ?? initialState;

  // Cumulative LTC payout BEFORE the payout-side view starts. Pre-payout
  // rows have payout == 0 by construction (a row only enters pre-payout
  // when its index is before its claimant's first paying row), so this is
  // almost always 0. Kept as a defensive sum for any future row that
  // classifies as pre-payout while still carrying a positive payout.
  const cumulativeBeforePayoutView = useMemo(() => {
    if (preMonths.length === 0) return 0;
    const series = getCumulativeLtcPayoutSeries(preMonths);
    return series[series.length - 1];
  }, [preMonths]);

  const hasPre = preMonths.length > 0;
  const hasPayout = payoutMonths.length > 0;
  const showDivider = hasPre && hasPayout;
  const payoutModeLabel = projection.terms.isIndemnity ? 'Indemnity' : 'Reimbursement';

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">Monthly breakdown</h2>
        {isJoint && (
          // Joint-only filter. Radix toggle-group's `single` mode emits an
          // empty string when the active item is re-clicked; coerce that
          // back to 'both' so the user can't end up in a no-selection
          // state that would render an empty table.
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={v => setView((v || 'both') as ClaimantView)}
            variant="outline"
            size="sm"
            aria-label="Show rows for"
          >
            <ToggleGroupItem value="both">Combined</ToggleGroupItem>
            <ToggleGroupItem value="client">Client</ToggleGroupItem>
            <ToggleGroupItem value="partner">Partner</ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>
      {hasPre && (
        <TimelineSection
          marker="pre"
          badge={
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
              Out-of-pocket period
            </span>
          }
        >
          <PrePayoutTable
            months={preMonths}
            initialState={initialState}
            terms={projection.terms}
            monthDisplayMap={monthDisplayMap}
          />
        </TimelineSection>
      )}
      {hasPayout && (
        <TimelineSection
          marker="post"
          continuesRail={showDivider}
          badge={
            hasPre ? (
              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                Benefits activated{payoutModeLabel ? ` · ${payoutModeLabel}` : ''}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                Month-by-month{payoutModeLabel ? ` · ${payoutModeLabel}` : ''}
              </span>
            )
          }
        >
          <ProjectionTable
            groups={payoutGroups}
            months={payoutMonths}
            openStateForFirstMonth={payoutOpenState}
            cumulativeLtcPayoutBeforeFirstMonth={cumulativeBeforePayoutView}
            groupByPolicyYear
          />
        </TimelineSection>
      )}
    </div>
  );
}

function TimelineSection({
  marker,
  badge,
  subtitle,
  continuesRail,
  children,
}: {
  marker: 'pre' | 'post';
  badge: ReactNode;
  subtitle?: string;
  continuesRail?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="relative pl-10">
      <span
        className={`absolute left-5 w-px bg-gray-200 ${continuesRail ? '-top-2' : 'top-5'} bottom-0`}
        aria-hidden
      />
      <span
        className={`absolute left-[14px] top-5 inline-flex h-3 w-3 items-center justify-center rounded-full ring-2 ring-white ${
          marker === 'post' ? 'bg-emerald-500' : 'border-2 border-gray-400 bg-white'
        }`}
        aria-hidden
      />
      <div className="px-2 py-4">
        <div className="mb-3 flex items-baseline gap-2">
          {badge}
          {subtitle && <span className="text-sm text-gray-500">{subtitle}</span>}
        </div>
        {children}
      </div>
    </section>
  );
}
